import config from '@/config';
import {ApiBase, InternationalizedField, Language} from '@/api/common';
import Hint from '@/api/hint';
import Video from '@/api/video';
import SentenceLink from './sentenceLink';
import StressMarker from './stressMarker';
import TranscriptArpabet from './transcriptArpabet';
import VideosI18n from '../api/videos-i18n';
import AltPhoneme from '../api/alt-phoneme';
import NextExercise from '../api/nextExercise';

const NO_UPLOAD_FIELDS = [
    'audio_link',
    'audio_alt_link',
    'image_link',
    'phoneme_objects',
    'stress_marker_objects',
    'video_objects',
    'sentence_link_objects',
    'global_hints_objects',
    'state',
    'audio_file',
    'audio_alt_file',
    'image_file',
    'next_exercise_objects',
];

export default class SpeakingAltContent extends ApiBase<SpeakingAltContent> {
    public static readonly SLUG: string = 'exercise-speaking-alt-content';
    private static readonly URL_BASE: string = config.API_ROOT + '/exercise-speaking-alt-content/';

    // Initializing these fields (even with undefined)
    // is necessary, so hasOwnAttribute can find them.
    public audio_path?: number = undefined;
    public audio_link?: string = undefined;
    public audio_alt_path?: number = undefined;
    public audio_alt_link?: string = undefined;
    public image_path?: number = undefined;
    public image_link?: string = undefined;
    public transcription?: string = undefined;
    public example?: string = undefined;
    public sentence?: string = undefined;
    public parent_exercise?: number = undefined;
    public phonemes?: number[] = undefined;
    public stress_markers?: number[] = undefined;
    public videos?: number[] = undefined;
    public sentence_links?: number[] = undefined;
    public description: InternationalizedField<string> = new InternationalizedField<string>();
    public reference_score?: number = undefined;

    public phoneme_objects: AltPhoneme[] = [];
    public stress_marker_objects: StressMarker[] = [];
    public video_objects: Video[] = [];
    public sentence_link_objects: SentenceLink[] = [];
    public transcript_arpabet: TranscriptArpabet[] = [];
    public global_hints: number[] = undefined;
    public global_hints_objects: InternationalizedField<Hint[]> = new InternationalizedField<Hint[]>();
    public videos_i18n: number[] = undefined;
    public videos_i18n_objects: InternationalizedField<VideosI18n[]> = new InternationalizedField<VideosI18n[]>();

    public state?: string = 'created';

    public audio_alt_file?: File|null = undefined;
    public audio_file?: File|null = undefined;
    public image_file?: File|null = undefined;

    public next_exercise_objects: NextExercise[] = [];
    public next_exercise: number[] = undefined;
    public sentence_i18n: InternationalizedField<string> = new InternationalizedField<string>();

    public constructor() {
        super({
            urlBase: SpeakingAltContent.URL_BASE,
            filteredFields: NO_UPLOAD_FIELDS,
        });
    }

    public assign(response: any): SpeakingAltContent {
        super.assign(response);

        if (response) {
            if ('global_hints' in response) {
                this.global_hints_objects = new InternationalizedField<Hint[]>(response.global_hints);
            }

            if ('videos_i18n' in response) {
                this.videos_i18n_objects = new InternationalizedField<VideosI18n[]>(response.videos_i18n);
            }

            if ('description' in response) {
                this.description = new InternationalizedField<string>(response.description);
            }

            if ('phonemes' in response) {
                this.phonemes = undefined;
                response.phonemes.forEach((phoneme: any) => {
                    this.phoneme_objects.push(new AltPhoneme().assign(phoneme));
                });
            }

            if ('stress_markers' in response) {
                this.stress_markers = undefined;
                response.stress_markers.forEach((stress_marker: any) => {
                    this.stress_marker_objects.push(new StressMarker().assign(stress_marker));
                });
            }

            if ('videos' in response) {
                this.videos = undefined;
                response.videos.forEach((video: any) => {
                    this.video_objects.push(new Video().assign(video));
                });
            }

            if ('sentence_links' in response) {
                this.sentence_links = undefined;
                response.sentence_links.forEach((sentence_link: any) =>  {
                    this.sentence_link_objects.push(new SentenceLink().assign(sentence_link));
                });
            }
            if ('transcript_arpabet' in response) {
                this.transcript_arpabet = [];
                response.transcript_arpabet.forEach((transcript_word: any) => {
                    this.transcript_arpabet.push(new TranscriptArpabet().assign(transcript_word));
                });
            }
            if (this.transcript_arpabet && (this.transcript_arpabet.length === 0 || this.transcript_arpabet[0].text === "") && this.sentence) {
                // if there is no transcript arpabet create it ith the sentence
                const word = new TranscriptArpabet();
                word.text = this.sentence;
                this.transcript_arpabet = [];
                this.transcript_arpabet.push(word);
            }
            if ('next_exercise' in response) {
                this.next_exercise = [];
                response.next_exercise.forEach((next_exercise: any) => {
                    this.next_exercise_objects.push(new NextExercise().assign(next_exercise));
                });
            }
             if ('sentence_i18n' in response) {
                 this.sentence_i18n = new InternationalizedField<string>(response.sentence_i18n);
             }
        }

        return this;
    }

    public async save(): Promise<number> {
        await this.saveTranscriptArpabet();
        if (!this.id) {
            return await super.save();
        } else {
            await Promise.all([
                this.savePhonemes(),
                this.saveStressMarkers(),
                this.saveVideos(),
                this.saveVideosI18n(),
                this.saveGlobalHints(),
                this.saveSentenceLinks(),
                this.saveNextExercises(),
            ]);

            return await super.save();
        }
    }

    public async saveTranscriptArpabet() {
        if (this.transcript_arpabet && this.transcript_arpabet[0].text === "") {
            if (!this.id) {
                return delete this.transcript_arpabet;
            } else {
                this.transcript_arpabet = [];
            }
        }
    }

    public async savePhonemes() {
        for (const phoneme of this.phoneme_objects) {
            if (phoneme.state === 'updated') {
                phoneme.parent_exercise = this.parent_exercise;
                if (!this.phonemes) {
                    this.phonemes = [];
                }
                this.phonemes.push(await phoneme.save());
            } else if (phoneme.state === 'deleted' && phoneme.id) {
                await phoneme.delete();
            }
        }
    }

    public async saveStressMarkers() {
        for (const stress_marker of this.stress_marker_objects) {
            if (stress_marker.state === 'updated') {
                stress_marker.parent_exercise = this.parent_exercise;
                if (!this.stress_markers) {
                    this.stress_markers = [];
                }
                this.stress_markers.push(await stress_marker.save());
            } else if (stress_marker.state === 'deleted' && stress_marker.id) {
                await stress_marker.delete();
            }
        }
    }

    public async saveVideos() {
        for (const video of this.video_objects) {
            if (video.state === 'updated') {
                video.parent_id = this.id;
                video.parent_model = 'exercisespeakingaltcontent';
                if (!this.videos) {
                    this.videos = [];
                }
                this.videos.push(await video.save());
            } else if (video.state === 'deleted' && video.id) {
                await video.delete();
            }
        }
    }

    public async saveVideosI18n() {
        this.videos_i18n = [];
        for (const lang of Language.LIST) {
            if (this.videos_i18n_objects.get(lang)) {
                for (let video of this.videos_i18n_objects.get(lang)) {
                    if(video.id && video.state !== 'deleted'){
                        this.videos_i18n.push(await video.id);
                    }
                    if (video.state === 'updated') {
                        video.parent_id = this.id;
                        video.parent_model = 'exercisespeakingaltcontent';
                        video = new VideosI18n().assign(video);
                        this.videos_i18n.push(await video.save());
                    } else if (video.state === 'deleted' && video.id) {
                        video = new VideosI18n().assign(video);
                        await video.delete();
                    }
                }
            }
        }
    }

    public async saveGlobalHints() {
        this.global_hints = [];
        for (const lang of Language.LIST) {
            if (this.global_hints_objects.get(lang)) {
                for (const hint of this.global_hints_objects.get(lang)) {
                    if(hint.id && hint.state !== 'deleted'){
                        this.global_hints.push(await hint.id);
                    }
                }
            }
        }
    }

    public async saveSentenceLinks() {
        for (const sentence_link of this.sentence_link_objects) {
            if (sentence_link.state === 'updated') {
                sentence_link.parent_id = this.id;
                sentence_link.parent_model = 'exercisespeakingaltcontent';
                if (!this.sentence_links) {
                    this.sentence_links = [];
                }
                this.sentence_links.push(await sentence_link.save());
            } else if (sentence_link.state === 'deleted' && sentence_link.id) {
                await sentence_link.delete();
            }
        }
    }

    public async saveNextExercises() {
        this.next_exercise = [];
        for (const next_exercise of this.next_exercise_objects) {
            if (next_exercise.id && next_exercise.state !== 'deleted') {
                this.next_exercise.push(await next_exercise.id);
            }
        }
    }
}
